import { Command } from 'commander';
import { existsSync, readFileSync } from 'fs';
import { execSync } from 'child_process';
import { join } from 'path';
import * as net from 'net';
import chalk from 'chalk';
import { loadConfig, configExists, repairConfig, type CloudGridConfig } from '../config.js';
import { readCredentials } from '../credentials.js';
import { printLegacyNotice } from '../legacy-notice.js';
import { isClaudeCodeInstalled } from '../services/claude-code.js';
import { Output } from '../output.js';
import { addOutputOption } from '../services/output-format.js';

interface CheckResult {
  label: string;
  ok: boolean;
  detail: string;
}

interface DoctorOpts {
  fix?: boolean;
  json?: boolean;
  output?: string;
}

export const doctorCommand = addOutputOption(
  new Command('doctor')
    .description('Run diagnostic checks')
    .option('--fix', 'Repair ~/.cloudgrid/config.yaml: validate its shape and seed missing defaults')
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (opts: DoctorOpts) => {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  // The legacy notice and banner are decoration — suppressed in structured
  // mode so the payload is a single clean JSON document.
  if (out.isHuman) {
    printLegacyNotice();
    console.log(`\n  ${chalk.bold('Cloud Grid Doctor')}\n`);
  }

  // --fix repairs the local config before the read-only checks run, so the
  // checks below reflect the post-repair state. Seeds a missing/empty
  // api_url (and domain) with the production defaults, preserving every
  // other key. Closes the QA-01 loop: a user whose config.yaml was left
  // partial by the startup update-check can `grid doctor --fix`
  // instead of hand-editing YAML.
  let repairNotes: string[] = [];
  if (opts.fix) {
    const { notes } = repairConfig();
    repairNotes = notes;
    if (out.isHuman) {
      console.log(`  ${chalk.bold('Repairing CLI config')}`);
      for (const note of notes) console.log(`    - ${note}`);
      console.log('');
    }
  }

  const results: CheckResult[] = [];

  // 1. Node.js version
  results.push(checkNode());

  // 2. Docker running
  results.push(await checkDocker());

  // 3. Claude Code
  results.push(checkClaudeCode());

  // 4. CLI config
  const configResult = checkConfig();
  results.push(configResult.result);

  // 5. API reachable
  if (configResult.config) {
    results.push(await checkApiReachable(configResult.config));

    // 6. API auth
    results.push(await checkApiAuth(configResult.config));
  }

  // 7. Common ports
  const ports = [3000, 5000, 27017, 6379];
  for (const port of ports) {
    results.push(await checkPort(port));
  }

  const passed = results.filter((r) => r.ok).length;
  const total = results.length;

  out.json(() => ({
    checks: results.map((r) => ({ label: r.label, ok: r.ok, detail: r.detail })),
    passed,
    total,
    ...(opts.fix ? { repair_notes: repairNotes } : {}),
  }));

  out.human(() => {
    for (const r of results) {
      const icon = r.ok ? chalk.green('\u2713') : chalk.red('\u2717');
      console.log(`  ${icon} ${r.label}: ${r.detail}`);
    }
    const warnings = total - passed;
    console.log(
      `\n  ${passed}/${total} checks passed.${warnings > 0 ? ` ${warnings} warning${warnings > 1 ? 's' : ''}.` : ''}\n`,
    );
    return undefined;
  });
});

function checkNode(): CheckResult {
  const version = process.version;
  const major = parseInt(version.slice(1), 10);
  return {
    label: 'Node.js',
    ok: major >= 20,
    detail: major >= 20 ? version : `${version} (need 20+)`,
  };
}

async function checkDocker(): Promise<CheckResult> {
  try {
    execSync('docker info', { stdio: 'pipe' });
    return { label: 'Docker', ok: true, detail: 'running' };
  } catch {
    return { label: 'Docker', ok: false, detail: 'not running or not installed' };
  }
}

function checkClaudeCode(): CheckResult {
  const installed = isClaudeCodeInstalled();
  return {
    label: 'Claude Code',
    ok: installed,
    detail: installed ? 'installed' : 'not found',
  };
}

function checkConfig(): { result: CheckResult; config: CloudGridConfig | null } {
  if (!configExists()) {
    return {
      result: { label: 'CLI config', ok: false, detail: '~/.cloudgrid/config.yaml not found' },
      config: null,
    };
  }
  try {
    const config = loadConfig();
    // v0.8 stores the JWT in ~/.cloudgrid/credentials, not config.yaml.
    // `config.jwt` is the v0.7 field — always undefined on the current line —
    // so requiring it here failed for every real user and skipped the
    // downstream API-reachable + auth checks. The auth check reads the token
    // from the credentials store instead (see checkApiAuth).
    if (!config.api_url) {
      return {
        result: { label: 'CLI config', ok: false, detail: 'missing api_url' },
        config: null,
      };
    }
    return {
      result: { label: 'CLI config', ok: true, detail: '~/.cloudgrid/config.yaml found' },
      config,
    };
  } catch {
    return {
      result: { label: 'CLI config', ok: false, detail: 'failed to parse config' },
      config: null,
    };
  }
}

async function checkApiReachable(config: CloudGridConfig): Promise<CheckResult> {
  try {
    const url = `${config.api_url.replace(/\/+$/, '')}/healthz`;
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
    return {
      label: 'API',
      ok: res.ok,
      detail: res.ok
        ? `${config.api_url} reachable (${res.status} OK)`
        : `${config.api_url} returned ${res.status}`,
    };
  } catch (err: any) {
    return { label: 'API', ok: false, detail: `${config.api_url} unreachable (${err.message})` };
  }
}

async function checkApiAuth(config: CloudGridConfig): Promise<CheckResult> {
  const creds = readCredentials();
  if (!creds?.jwt) {
    return { label: 'API auth', ok: false, detail: 'not signed in (run: grid login)' };
  }
  try {
    const url = `${config.api_url.replace(/\/+$/, '')}/apps`;
    const res = await fetch(url, {
      headers: { 'authorization': `Bearer ${creds.jwt}` },
      signal: AbortSignal.timeout(5000),
    });
    return {
      label: 'API auth',
      ok: res.ok,
      detail: res.ok ? 'valid' : `rejected (${res.status})`,
    };
  } catch (err: any) {
    return { label: 'API auth', ok: false, detail: `failed (${err.message})` };
  }
}

function checkPort(port: number): Promise<CheckResult> {
  return new Promise((resolve) => {
    const server = net.createServer();
    server.once('error', (err: NodeJS.ErrnoException) => {
      if (err.code === 'EADDRINUSE') {
        resolve({ label: `Port ${port}`, ok: false, detail: 'in use' });
      } else {
        resolve({ label: `Port ${port}`, ok: false, detail: err.message });
      }
    });
    server.once('listening', () => {
      server.close(() => {
        resolve({ label: `Port ${port}`, ok: true, detail: 'free' });
      });
    });
    server.listen(port, '127.0.0.1');
  });
}
